// Import axios config to ensure it's loaded
import '../../config/axios';
import { AxiosInstance } from 'axios';
import { PaginatedResponse, Invoice } from '../../types';

// Import service instances
import { pricingService } from './pricing.service';
import { contractService } from './contract.service';
import { customerService } from './customer.service';
import { serverService } from './server.service';
import { userService } from './user.service';
import { invoiceService } from './invoice.service';
import { modernInvoiceService } from './modern-invoice.service';
import { companyService } from './company.service';
import { analyticsService } from './analytics.service';
import { importService } from './import.service';
import { healthService } from './health.service';
import { boletoService } from './boleto.service';
import { crudService } from './crud.service';
import { userAssignmentService } from './userAssignment.service';
import { contractRecipientService } from './contractRecipient.service';
import { reviewAutomationService } from './reviewAutomation.service';
import { contractInvoicePaymentService } from './contract-invoice-payment.service';

// Export all individual services
export * from './base.service';
export * from './pricing.service';
export * from './contract.service';
export * from './contractRecipient.service';
export * from './customer.service';
export * from './server.service';
export * from './user.service';
export * from './userAssignment.service';
export * from './userAssignment.service';
export * from './invoice.service';
export * from './modern-invoice.service';
export * from './company.service';
export * from './analytics.service';
export * from './import.service';
export * from './health.service';
export * from './boleto.service';
export * from './crud.service';
export * from './reviewAutomation.service';
export * from './contract-invoice-payment.service';

/**
 * Unified API service that aggregates all domain-specific services
 * This maintains backward compatibility with the original api.ts structure
 */
class ApiService {
  // Service instances
  public pricing = pricingService;
  public contracts = contractService;
  public customers = customerService;
  public servers = serverService;
  public users = userService;
  public userAssignments = userAssignmentService;
  public invoices = invoiceService;
  public modernInvoices = modernInvoiceService;
  public companies = companyService;
  public analytics = analyticsService;
  public import = importService;
  public health = healthService;
  public boleto = boletoService;
  public crud = crudService;
  public reviewAutomation = reviewAutomationService;
  public contractInvoicePaymentService = contractInvoicePaymentService;

  // Get base URL (from any service instance)
  getBaseUrl(): string {
    return this.health.getBaseUrl();
  }

  // Get axios instance for direct access (backward compatibility)
  get axiosInstance(): AxiosInstance {
    // Return the axios instance from any of the services (they all share the same instance)
    return (this.health as any).axiosInstance;
  }

  // Public method for direct API access (backward compatibility)
  async get<T>(url: string): Promise<T> {
    return (this.health as any).get(url);
  }

  async post<T, D = any>(url: string, data?: D): Promise<T> {
    return (this.health as any).post(url, data);
  }

  async put<T, D = any>(url: string, data?: D): Promise<T> {
    return (this.health as any).put(url, data);
  }

  async delete<T>(url: string): Promise<T> {
    return (this.health as any).delete(url);
  }

  async deleteWithData<T, D = any>(url: string, data?: D): Promise<T> {
    return (this.health as any).deleteWithData(url, data);
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.health.healthCheck();
  }

  // Pricing Headers
  async getPricingHeaders(skip: number = 0, limit: number = 10, includeInactive: boolean = false): Promise<any[]> {
    return this.pricing.getPricingHeaders(skip, limit, includeInactive);
  }

  async getPricingHeader(id: number): Promise<any> {
    return this.pricing.getPricingHeader(id);
  }

  async createPricingHeader(header: any): Promise<any> {
    return this.pricing.createPricingHeader(header);
  }

  async updatePricingHeader(id: number, header: any): Promise<any> {
    return this.pricing.updatePricingHeader(id, header);
  }

  async deletePricingHeader(id: number): Promise<void> {
    return this.pricing.deletePricingHeader(id);
  }

  // Plans
  async getPlans(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.pricing.getPlans(page, pageSize);
  }

  async getAllPlans(limit: number = 100): Promise<any[]> {
    return this.pricing.getAllPlans(limit);
  }

  async getPlan(id: number): Promise<any> {
    return this.pricing.getPlan(id);
  }

  async createPlan(plan: any): Promise<any> {
    return this.pricing.createPlan(plan);
  }

  async updatePlan(id: number, plan: any): Promise<any> {
    return this.pricing.updatePlan(id, plan);
  }

  async deletePlan(id: number): Promise<void> {
    return this.pricing.deletePlan(id);
  }

  async getPlanFeatures(planId: number): Promise<any[]> {
    return this.pricing.getPlanFeatures(planId);
  }

  // Price Lists
  async getPriceLists(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.pricing.getPriceLists(page, pageSize);
  }

  async getPriceList(id: number): Promise<any> {
    return this.pricing.getPriceList(id);
  }

  async getPriceListsByServer(serverId: number, limit: number = 500): Promise<any[]> {
    return this.pricing.getPriceListsByServer(serverId, limit);
  }

  async getPriceListGroups(is_active: boolean | null = true): Promise<any[]> {
    return this.pricing.getPriceListGroups(is_active);
  }

  async createPriceListGroup(priceListGroup: any): Promise<any> {
    return this.pricing.createPriceListGroup(priceListGroup);
  }

  async updatePriceListGroup(id: number, priceListGroup: any): Promise<any> {
    return this.pricing.updatePriceListGroup(id, priceListGroup);
  }

  async deletePriceListGroup(id: number): Promise<void> {
    return this.pricing.deletePriceListGroup(id);
  }

  async createPriceList(priceList: any): Promise<any> {
    return this.pricing.createPriceList(priceList);
  }

  async updatePriceList(id: number, priceList: any): Promise<any> {
    return this.pricing.updatePriceList(id, priceList);
  }

  async deletePriceList(id: number): Promise<void> {
    return this.pricing.deletePriceList(id);
  }

  // Contracts
  async getContracts(
    page: number = 1, 
    pageSize: number = 10, 
    isActive?: boolean, 
    customerId?: number,
    search?: string,
    hideInactiveExpired?: boolean,
    hideWithoutCustomer?: boolean,
    orderBy?: string,
    orderDir: 'asc' | 'desc' = 'asc',
    status?: 'all' | 'active' | 'deleted'
  ): Promise<any> {
    return this.contracts.getContracts(page, pageSize, isActive, customerId, search, hideInactiveExpired, hideWithoutCustomer, orderBy, orderDir, status);
  }

  async getContract(id: string): Promise<any> {
    return this.contracts.getContract(id);
  }

  async createContract(contract: any): Promise<any> {
    return this.contracts.createContract(contract);
  }

  async updateContract(id: string, contract: any): Promise<any> {
    return this.contracts.updateContract(id, contract);
  }

  async deleteContract(id: string): Promise<void> {
    return this.contracts.deleteContract(id);
  }

  async bulkDeleteContracts(contractIds: string[], softDelete: boolean = true): Promise<any> {
    return this.contracts.bulkDeleteContracts(contractIds, softDelete);
  }

  async getContractLines(contractId: string): Promise<any[]> {
    return this.contracts.getContractLines(contractId);
  }

  // Contract Lines
  async createContractLine(contractLine: any): Promise<any> {
    return this.contracts.createContractLine(contractLine);
  }

  async updateContractLine(id: string, contractLine: any): Promise<any> {
    return this.contracts.updateContractLine(id, contractLine);
  }

  async deleteContractLine(id: string): Promise<void> {
    return this.contracts.deleteContractLine(id);
  }

  // Customers
  async getCustomers(
    page: number = 1, 
    pageSize: number = 10, 
    customerType?: 'controller' | 'supervisor',
    paymentStatus?: string,
    isSupervised?: boolean,
    search?: string,
    isActive?: boolean
  ): Promise<any> {
    return this.customers.getCustomers(page, pageSize, customerType, paymentStatus, isSupervised, search, isActive);
  }

  async getCustomer(id: number): Promise<any> {
    return this.customers.getCustomer(id);
  }

  async createCustomer(customer: any): Promise<any> {
    return this.customers.createCustomer(customer);
  }

  async updateCustomer(id: number, customer: any): Promise<any> {
    return this.customers.updateCustomer(id, customer);
  }

  async deleteCustomer(id: number): Promise<void> {
    return this.customers.deleteCustomer(id);
  }

  async bulkDeleteCustomers(customerIds: number[], softDelete: boolean = true): Promise<any> {
    return this.customers.bulkDeleteCustomers(customerIds, softDelete);
  }

  async getUnsupervisedCustomers(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.customers.getUnsupervisedCustomers(page, pageSize);
  }

  async removeControllerFromSupervisor(controllerId: number): Promise<any> {
    return this.customers.removeControllerFromSupervisor(controllerId);
  }

  async updateAllSupervisionStatus(): Promise<any> {
    return this.customers.updateAllSupervisionStatus();
  }

  // Pricing items (legacy)
  async getPricingItems(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.pricing.getPricingItems(page, pageSize);
  }

  async getPricingItem(id: string): Promise<any> {
    return this.pricing.getPricingItem(id);
  }

  async createPricingItem(item: any): Promise<any> {
    return this.pricing.createPricingItem(item);
  }

  // Stripe Payment Intent
  async createStripePaymentIntent(invoiceId: string): Promise<any> {
    return this.post(`/api/v1/invoices/${invoiceId}/create-payment-intent`, {});
  }

  async getPaymentIntentEvents(paymentIntentId: string): Promise<any> {
    return this.contractInvoicePaymentService.getPaymentIntentEvents(paymentIntentId);
  }

  async getInvoiceStripePaymentStatus(invoiceId: string): Promise<any> {
    return this.contractInvoicePaymentService.getInvoiceStripePaymentStatus(invoiceId);
  }

  async updatePricingItem(id: string, item: any): Promise<any> {
    return this.pricing.updatePricingItem(id, item);
  }

  async deletePricingItem(id: string): Promise<void> {
    return this.pricing.deletePricingItem(id);
  }

  // Analytics
  async getAnalyticsDashboard(period: string): Promise<any> {
    return this.analytics.getAnalyticsDashboard(period);
  }

  // Monitoring
  async getMonitoringDashboard(): Promise<any> {
    return this.analytics.getMonitoringDashboard();
  }

  // Servers
  async getServers(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.servers.getServers(page, pageSize);
  }

  async getAllServers(limit: number = 100): Promise<any[]> {
    return this.servers.getAllServers(limit);
  }

  async getServer(id: string): Promise<any> {
    return this.servers.getServer(id);
  }

  async createServer(server: any): Promise<any> {
    return this.servers.createServer(server);
  }

  async updateServer(id: string, server: any): Promise<any> {
    return this.servers.updateServer(id, server);
  }

  async deleteServer(id: string): Promise<void> {
    return this.servers.deleteServer(id);
  }

  async regenerateServerCredentials(id: string): Promise<any> {
    return this.servers.regenerateServerCredentials(id);
  }

  async generateBillingBackendToken(id: string): Promise<any> {
    return this.servers.generateBillingBackendToken(id);
  }

  async uploadFile(serverId: string, file: File, fileType: 'supervisor' | 'controller'): Promise<any> {
    return this.servers.uploadFile(serverId, file, fileType);
  }

  // Users
  async getUsers(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.users.getUsers(page, pageSize);
  }

  async getAllUsers(limit: number = 100): Promise<any[]> {
    return this.users.getAllUsers(limit);
  }

  async getUser(id: number): Promise<any> {
    return this.users.getUser(id);
  }

  async createUser(user: any): Promise<any> {
    return this.users.createUser(user);
  }

  async updateUser(id: number, user: any): Promise<any> {
    return this.users.updateUser(id, user);
  }

  async deleteUser(id: number): Promise<void> {
    return this.users.deleteUser(id);
  }

  // Supervisors
  async getSupervisors(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.servers.getSupervisors(page, pageSize);
  }

  async getAllSupervisors(limit: number = 100): Promise<any[]> {
    return this.servers.getAllSupervisors(limit);
  }

  async getSupervisor(id: number): Promise<any> {
    return this.servers.getSupervisor(id);
  }

  async createSupervisor(supervisor: any): Promise<any> {
    return this.servers.createSupervisor(supervisor);
  }

  async updateSupervisor(id: number, supervisor: any): Promise<any> {
    return this.servers.updateSupervisor(id, supervisor);
  }

  async deleteSupervisor(id: number): Promise<void> {
    return this.servers.deleteSupervisor(id);
  }

  // Controllers
  async getControllers(page: number = 1, pageSize: number = 10): Promise<any> {
    return this.servers.getControllers(page, pageSize);
  }

  async getAllControllers(limit: number = 100): Promise<any[]> {
    return this.servers.getAllControllers(limit);
  }

  async getController(id: number): Promise<any> {
    return this.servers.getController(id);
  }

  async getControllersBySupervisor(supervisorId: number): Promise<any[]> {
    return this.servers.getControllersBySupervisor(supervisorId);
  }

  async createController(controller: any): Promise<any> {
    return this.servers.createController(controller);
  }

  async updateController(id: number, controller: any): Promise<any> {
    return this.servers.updateController(id, controller);
  }

  async deleteController(id: number): Promise<void> {
    return this.servers.deleteController(id);
  }

  // Invoices
  async createInvoice(request: any): Promise<any> {
    return this.invoices.createInvoice(request);
  }

  async getInvoice(id: string): Promise<any> {
    return this.invoices.getInvoice(id);
  }

  async updateInvoice(id: string, invoice: any): Promise<any> {
    return this.invoices.updateInvoice(id, invoice);
  }

  async updateInvoiceStatus(id: string, data: any): Promise<any> {
    return this.invoices.updateInvoiceStatus(id, data);
  }

  async getInvoiceByName(name: string): Promise<any> {
    return this.invoices.getInvoiceByName(name);
  }

  async sendTestEmail(
    invoiceId: string,
    recipientEmail: string,
    language: string,
    emailType: string = 'invoice'
  ): Promise<{
    success: boolean;
    message: string;
    email_id?: string;
  }> {
    return this.invoices.sendTestEmail(invoiceId, recipientEmail, language, emailType);
  }

  async getInvoices(
    page: number = 1, 
    pageSize: number = 100,
    search?: string,
    status?: string,
    customerId?: number,
    contractId?: string,
    paymentStatus?: string
  ): Promise<PaginatedResponse<Invoice>> {
    return this.invoices.getInvoices(page, pageSize, search, status, customerId, contractId, paymentStatus);
  }

  async getInvoicesByContract(contractId: string): Promise<any[]> {
    return this.invoices.getInvoicesByContract(contractId);
  }

  async getInvoicesByCustomer(customerId: number): Promise<any[]> {
    return this.invoices.getInvoicesByCustomer(customerId);
  }

  async getInvoicesByContractsBatch(contractIds: string[]): Promise<any> {
    return this.invoices.getInvoicesByContractsBatch(contractIds);
  }

  async downloadContractInvoiceReport(
    contractId: string,
    language: string = 'en',
    finalizedOnly: boolean = true
  ): Promise<Blob> {
    return this.invoices.downloadContractInvoiceReport(contractId, language, finalizedOnly);
  }

  async deleteInvoice(id: string): Promise<any> {
    return this.invoices.deleteInvoice(id);
  }

  // Payment status management
  async updateAllPaymentStatuses(): Promise<any> {
    return this.invoices.updateAllPaymentStatuses();
  }

  async checkPayments(invoiceIds?: string[]): Promise<any> {
    return this.invoices.checkPayments(invoiceIds);
  }

  async getOverdueInvoices(daysOverdue?: number): Promise<any[]> {
    return this.invoices.getOverdueInvoices(daysOverdue);
  }

  async getUpcomingDueInvoices(daysAhead?: number): Promise<any[]> {
    return this.invoices.getUpcomingDueInvoices(daysAhead);
  }

  async getPaymentStatusSummary(): Promise<any> {
    return this.invoices.getPaymentStatusSummary();
  }

  async getInvoiceBoletos(invoiceId: string): Promise<any[]> {
    return this.invoices.getInvoiceBoletos(invoiceId);
  }

  // Modern Invoices
  async getCombinedInvoiceData(invoiceId: string): Promise<any> {
    return this.modernInvoices.getCombinedInvoiceData(invoiceId);
  }

  async generateModernPDF(invoiceId: string, language: string = 'en'): Promise<Blob> {
    return this.modernInvoices.generateModernPDF(invoiceId, language);
  }

  getModernPDFUrl(invoiceId: string, language: string = 'en'): string {
    return this.modernInvoices.getModernPDFUrl(invoiceId, language);
  }

  async downloadModernPDF(invoiceId: string, language: string = 'en', filename?: string): Promise<void> {
    return this.modernInvoices.downloadModernPDF(invoiceId, language, filename);
  }

  async openModernPDF(invoiceId: string, language: string = 'en'): Promise<void> {
    return this.modernInvoices.openModernPDF(invoiceId, language);
  }

  // Companies
  async getCompanies(skip: number = 0, limit: number = 100, includeInactive: boolean = false): Promise<any[]> {
    return this.companies.getCompanies(skip, limit, includeInactive);
  }

  async getCompany(id: number): Promise<any> {
    return this.companies.getCompany(id);
  }

  async createCompany(company: any): Promise<any> {
    return this.companies.createCompany(company);
  }

  async updateCompany(id: number, company: any): Promise<any> {
    return this.companies.updateCompany(id, company);
  }

  async deleteCompany(id: number): Promise<void> {
    return this.companies.deleteCompany(id);
  }

  async getCompanyContracts(companyId: number, skip: number = 0, limit: number = 100): Promise<any[]> {
    return this.companies.getCompanyContracts(companyId, skip, limit);
  }

  async uploadCompanyLogo(id: number, formData: FormData): Promise<{ logo_url: string }> {
    return this.companies.uploadCompanyLogo(id, formData);
  }

  // Data Import
  async importControllerJson(serverId?: number): Promise<any> {
    return this.import.importControllerJson(serverId);
  }

  async importSupervisorJson(serverId?: number): Promise<any> {
    return this.import.importSupervisorJson(serverId);
  }

  async importCurrentSupervisorJson(serverId?: number): Promise<any> {
    return this.import.importCurrentSupervisorJson(serverId);
  }

  async importFile(file: File, serverId?: number): Promise<any> {
    return this.import.importFile(file, serverId);
  }

  async importViaWebhook(data: any, serverId?: number): Promise<any> {
    return this.import.importViaWebhook(data, serverId);
  }

  // Boleto
  async checkBoletoEligibility(invoiceId: string): Promise<any> {
    return this.boleto.checkBoletoEligibility(invoiceId);
  }

  async createBoletoForInvoice(invoiceId: string, notificationUrl?: string, forceCreate?: boolean, dueDate?: string): Promise<any> {
    return this.boleto.createBoletoForInvoice(invoiceId, notificationUrl, forceCreate, dueDate);
  }

  async getBoletoStatus(invoiceId: string): Promise<any> {
    return this.boleto.getBoletoStatus(invoiceId);
  }

  async generateInvoiceWithBoletoPDF(invoiceId: string, language?: string, createBoleto?: boolean, forceCreate?: boolean, notificationUrl?: string): Promise<Blob> {
    return this.boleto.generateInvoiceWithBoletoPDF(invoiceId, language, createBoleto, forceCreate, notificationUrl);
  }

  getInvoiceWithBoletoPDFUrl(invoiceId: string, language?: string, createBoleto?: boolean, forceCreate?: boolean, notificationUrl?: string): string {
    return this.boleto.getInvoiceWithBoletoPDFUrl(invoiceId, language, createBoleto, forceCreate, notificationUrl);
  }

  async downloadInvoiceWithBoletoPDF(invoiceId: string, language?: string, createBoleto?: boolean, forceCreate?: boolean, notificationUrl?: string, filename?: string): Promise<void> {
    return this.boleto.downloadInvoiceWithBoletoPDF(invoiceId, language, createBoleto, forceCreate, notificationUrl, filename);
  }

  async openInvoiceWithBoletoPDF(invoiceId: string, language?: string, createBoleto?: boolean, forceCreate?: boolean, notificationUrl?: string): Promise<void> {
    return this.boleto.openInvoiceWithBoletoPDF(invoiceId, language, createBoleto, forceCreate, notificationUrl);
  }

  async getBoletoAnalytics(period?: string): Promise<any> {
    return this.boleto.getBoletoAnalytics(period);
  }

  // Contract Recipients
  async createContractRecipient(recipientData: any): Promise<any> {
    return contractRecipientService.createContractRecipient(recipientData);
  }

  async getContractRecipient(id: string): Promise<any> {
    return contractRecipientService.getContractRecipient(id);
  }

  async updateContractRecipient(id: string, recipientData: any): Promise<any> {
    return contractRecipientService.updateContractRecipient(id, recipientData);
  }

  async deleteContractRecipient(id: string, deletedBy: string): Promise<any> {
    return contractRecipientService.deleteContractRecipient(id, deletedBy);
  }

  async getRecipientsByContract(contractId: string, skip: number = 0, limit: number = 100): Promise<any> {
    return contractRecipientService.getRecipientsByContract(contractId, skip, limit);
  }

  async getContractUpdateRecipients(contractId: string): Promise<any> {
    return contractRecipientService.getContractUpdateRecipients(contractId);
  }

  async getInvoiceRecipients(contractId: string): Promise<any> {
    return contractRecipientService.getInvoiceRecipients(contractId);
  }

  async activateContractRecipient(id: string, activatedBy: string): Promise<any> {
    return contractRecipientService.activateContractRecipient(id, activatedBy);
  }

  async deactivateContractRecipient(id: string, deactivatedBy: string): Promise<any> {
    return contractRecipientService.deactivateContractRecipient(id, deactivatedBy);
  }

  async bulkCreateContractRecipients(recipientsData: any[]): Promise<any> {
    return contractRecipientService.bulkCreateContractRecipients(recipientsData);
  }
}

// Export singleton instance for backward compatibility
export const apiService = new ApiService();
export default apiService;
